import { computed, type Ref } from 'vue'
import {
  arrivalRequirementState as arrivalRequirementStateOf,
  canEnterStep,
  hasValueForStep as hasValueForStepOf,
  isLastStep as isLastStepOf,
  isStepComplete as isStepCompleteOf,
  nextStep,
  prevStep,
  stepByKey,
  stepIndex,
  visibleSteps as visibleStepsOf,
  type ArrivalRequirementState
} from '../model/portcall-wizard'
import { isEtaFieldComplete, type EtaFieldValue } from '../model/eta-bridge'
import {
  PORTCALL_STEPS,
  type ArrivalStepKey,
  type ListStepKey,
  type PortcallStep
} from '../components/providers/create-portcall-provider/step-flow/create-portcall-steps'
import type { CreatePortcallFormValues } from './useCreatePortcallForm'

/**
 * The reactive inputs the wizard derives from. The composable only reads
 * them, so read-only refs (e.g. the `computed`s from `useCreatePortcallForm`)
 * are accepted.
 */
export interface WizardInputs {
  /** The step the user is currently on (component-owned navigation state). */
  currentStepKey: Readonly<Ref<PortcallStep['key']>>
  /** The live form values. */
  formValues: Readonly<Ref<CreatePortcallFormValues>>
  /** Whether the selected port captures a separate pilot-station ETA. */
  pilotStationActive: Readonly<Ref<boolean>>
  /** Whether every required field + the ETA section is satisfied. */
  canSubmit: Readonly<Ref<boolean>>
}

/**
 * The blessed MVVM seam over `model/portcall-wizard.ts`: turns the reactive
 * wizard inputs into the computeds + helpers `CreatePortcallStepFlow.vue`
 * binds to, so the component carries no step-machine decisions itself.
 */
export function usePortcallWizard(inputs: WizardInputs) {
  const { currentStepKey, formValues, pilotStationActive, canSubmit } = inputs

  const visibleSteps = computed(() =>
    visibleStepsOf(PORTCALL_STEPS, pilotStationActive.value)
  )
  const currentStep = computed<PortcallStep>(
    () =>
      stepByKey(PORTCALL_STEPS, currentStepKey.value) ??
      (PORTCALL_STEPS[0] as PortcallStep)
  )
  const currentIndex = computed(() =>
    stepIndex(visibleSteps.value, currentStepKey.value)
  )

  function isLastStep(step: PortcallStep): boolean {
    return isLastStepOf(visibleSteps.value, step.key)
  }
  function nextVisible(): PortcallStep | undefined {
    return nextStep(visibleSteps.value, currentStepKey.value)
  }
  function prevVisible(): PortcallStep | undefined {
    return prevStep(visibleSteps.value, currentStepKey.value)
  }
  function canEnter(step: PortcallStep): boolean {
    return canEnterStep(step, {
      hasAgency: !!formValues.value.agencyId,
      hasPort: !!formValues.value.portOfCallLocationCode
    })
  }
  /** The form value backing a step, or `undefined` for the fieldless summary. */
  function fieldValue(step: PortcallStep) {
    return step.kind === 'summary'
      ? undefined
      : formValues.value[step.key as ArrivalStepKey | ListStepKey]
  }
  /** The other arrival slot, so the port step can see the pilot ETA (or vice versa). */
  function siblingEta(step: PortcallStep): EtaFieldValue | undefined {
    if (step.key === 'etaAtPort') return formValues.value.etaAtPilotStation
    if (step.key === 'etaAtPilotStation') return formValues.value.etaAtPort
    return undefined
  }
  function isStepComplete(step: PortcallStep): boolean {
    return isStepCompleteOf(step, fieldValue(step), {
      canSubmit: canSubmit.value,
      pilotStationActive: pilotStationActive.value,
      sibling: siblingEta(step)
    })
  }
  function hasValueForStep(step: PortcallStep): boolean {
    return hasValueForStepOf(step, fieldValue(step))
  }
  /** Looks a step up by key across the full sequence (drives `goStep`). */
  function findStep(key: string): PortcallStep | undefined {
    return stepByKey(PORTCALL_STEPS, key)
  }
  /** The requirement-banner state for an arrival step (copy stays in the view). */
  function arrivalRequirementState(
    step: PortcallStep
  ): ArrivalRequirementState {
    return arrivalRequirementStateOf(step, {
      currentComplete: isEtaFieldComplete(
        formValues.value[step.key as ArrivalStepKey]
      ),
      pilotStationActive: pilotStationActive.value,
      pilotComplete: isEtaFieldComplete(formValues.value.etaAtPilotStation)
    })
  }

  return {
    visibleSteps,
    currentStep,
    currentIndex,
    isLastStep,
    nextVisible,
    prevVisible,
    canEnter,
    isStepComplete,
    hasValueForStep,
    findStep,
    arrivalRequirementState
  }
}
